import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

interface ManualCreationFormProps {
  data: any;
  onUpdate: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

interface Unit {
  block_name: string;
  unit_number: string;
  floor: number;
  unit_type: string;
  is_duplex: boolean;
  is_triplex: boolean;
  floor_span: number;
  area: number;
  rooms: number;
  bedrooms: number;
  bathrooms: number;
  orientation: string;
  view: string;
  has_balcony: boolean;
  balcony_area: number;
  has_parking: boolean;
  parking_included: boolean;
  parking_number: string;
  parking_type: string;
  base_price: number;
  final_price: number;
  status: string;
}

export const ManualCreationForm: React.FC<ManualCreationFormProps> = ({
  data,
  onUpdate,
  onNext,
  onBack,
}) => {
  const [units, setUnits] = useState<Unit[]>(data.units || []);
  const [errors, setErrors] = useState<any>({});

  const addNewUnit = () => {
    const newUnit: Unit = {
      block_name: data.blocks[0]?.block_name || 'A',
      unit_number: '',
      floor: 0,
      unit_type: 'f3',
      is_duplex: false,
      is_triplex: false,
      floor_span: 1,
      area: 85,
      rooms: 3,
      bedrooms: 2,
      bathrooms: 1,
      orientation: 'sud',
      view: 'Dégagée',
      has_balcony: true,
      balcony_area: 8,
      has_parking: true,
      parking_included: true,
      parking_number: '',
      parking_type: 'sous-sol',
      base_price: 11000000,
      final_price: 11000000,
      status: 'available',
    };
    setUnits([...units, newUnit]);
  };

  const removeUnit = (index: number) => {
    setUnits(units.filter((_, i) => i !== index));
  };

  const updateUnit = (index: number, field: string, value: any) => {
    const updatedUnits = [...units];
    updatedUnits[index] = { ...updatedUnits[index], [field]: value };
    setUnits(updatedUnits);
  };

  const handleSubmit = () => {
    const newErrors: any = {};
    units.forEach((unit, index) => {
      if (!unit.unit_number) {
        newErrors[`unit_${index}_number`] = 'Numéro obligatoire';
      }
      if (unit.final_price <= 0) {
        newErrors[`unit_${index}_price`] = 'Prix obligatoire';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onUpdate({ ...data, units });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Création manuelle des lots
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Ajoutez chaque lot individuellement avec tous les détails
        </p>
      </div>

      {units.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Aucun lot créé pour le moment
          </p>
          <button
            onClick={addNewUnit}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Ajouter le premier lot
          </button>
        </div>
      )}

      {units.map((unit, index) => (
        <div key={index} className="border dark:border-gray-700 rounded-lg p-6 bg-white dark:bg-gray-800">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Lot {index + 1}
            </h3>
            <button
              onClick={() => removeUnit(index)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bloc *
              </label>
              <select
                value={unit.block_name}
                onChange={(e) => updateUnit(index, 'block_name', e.target.value)}
                className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                {data.blocks.map((block: any) => (
                  <option key={block.block_name} value={block.block_name}>
                    {block.block_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Numéro *
              </label>
              <input
                type="text"
                value={unit.unit_number}
                onChange={(e) => updateUnit(index, 'unit_number', e.target.value)}
                className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                placeholder="A0101"
              />
              {errors[`unit_${index}_number`] && (
                <p className="text-red-600 text-xs mt-1">{errors[`unit_${index}_number`]}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Étage
              </label>
              <input
                type="number"
                value={unit.floor}
                onChange={(e) => updateUnit(index, 'floor', parseInt(e.target.value))}
                className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type *
              </label>
              <select
                value={unit.unit_type}
                onChange={(e) => updateUnit(index, 'unit_type', e.target.value)}
                className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="studio">Studio</option>
                <option value="f2">F2</option>
                <option value="f3">F3</option>
                <option value="f4">F4</option>
                <option value="f5">F5</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Surface (m²) *
              </label>
              <input
                type="number"
                value={unit.area}
                onChange={(e) => updateUnit(index, 'area', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prix (DA) *
              </label>
              <input
                type="number"
                value={unit.final_price}
                onChange={(e) => updateUnit(index, 'final_price', parseInt(e.target.value))}
                className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
              {errors[`unit_${index}_price`] && (
                <p className="text-red-600 text-xs mt-1">{errors[`unit_${index}_price`]}</p>
              )}
            </div>
          </div>
        </div>
      ))}

      {units.length > 0 && (
        <button
          onClick={addNewUnit}
          className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500 transition-colors flex items-center justify-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Ajouter un autre lot</span>
        </button>
      )}

      <div className="flex justify-between pt-6 border-t dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          ← Retour
        </button>

        <button
          onClick={handleSubmit}
          disabled={units.length === 0}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          Suivant : Photos →
        </button>
      </div>
    </div>
  );
};
